import React, { FC, useEffect } from 'react'
import { GetStaticProps } from 'next'
import {
  getPageByUrlSlug,
  SingleItemResponse,
  getKontentImage,
  getPageByCodename,
  MultipleItemResponse,
  getCatchAllRolexProductPaths
} from '@lib/kontent'
import { RolexProduct } from '@lib/kontent/models/RolexProduct'
import { RolexFamilyInfoProductPage } from '@lib/kontent/models/RolexFamilyInfoProductPage'
import { Page, pickSeoProps } from '@components/Page'
import RolexPage from '@components/_rolex/Page'
import ProductInfo from '@components/_rolex/ProductInfo'
import ModelAvailability from '@components/_rolex/ModelAvailability'
import KeyModelFeature from '@components/_rolex/KeyModelFeature'
import ProductProperties from '@components/_rolex/ProductProperties'
import { VideoPlayer, Text } from '@rolex-ui'
import { Box } from '@components/gassan-ui'
import { rem } from 'polished'
import PushBanner from '@components/_rolex/PushBanner'
import { useLocalization } from '@components/Localization'
import ContactForm from '@components/_rolex/ContactForm'
import RelatedProductsSlider from '@components/_rolex/Sliders/RelatedProductsSlider'
import { getSpecifiedRolexProducts } from '@lib/kontent/getSpecifiedRolexProducts'
import Head from 'next/head'

type Props = SingleItemResponse<RolexProduct> & {
  familyInfo?: SingleItemResponse<RolexFamilyInfoProductPage>
  relatedProducts?: MultipleItemResponse<RolexProduct>
  fid: string
}

const RolexDetail: FC<Props> = ({ item, modular_content, familyInfo, relatedProducts, fid }) => {
  const { messages, lang } = useLocalization()
  useEffect(() => {
    window['productModelPageRLX'] = `${fid};${item.elements.url_slug.value};`
  }, []) // eslint-disable-line

  let keyModelFeatures = [
    {
      title: item.elements.feature_1_title.value,
      text: item.elements.feature_1_text.value,
      image: item.elements.feature_1_image
    },

    {
      title: item.elements.feature_2_title.value,
      text: item.elements.feature_2_text.value,
      image: item.elements.feature_2_image
    },

    {
      title: item.elements.feature_3_title.value,
      text: item.elements.feature_3_text.value,
      image: item.elements.feature_3_image
    }
  ]

  return (
    <Page
      {...pickSeoProps(item.elements)}
      slug={`rolex/${fid}/${item.elements.url_slug.value}`}
      title={`Rolex ${item.elements.title.value} ${item.elements.url_slug.value} | GASSAN Diamonds`}
      description={familyInfo.item.elements.seo_description.value}
    >
      <Head>
        <script
          type="text/javascript"
          id="rlx-plaques"
          data-dak="3749a28a87220925506fa122a876c80e"
          src="https://static.rolex.com/rlx-plaques-v2/js/plaquespage.js"
        />
      </Head>
      <RolexPage>
        <ProductInfo
          preTitle="Rolex"
          title={item.elements.title.value}
          price={item.elements.price.value}
          shortDescription={item.elements.short_description.value}
          sources={item.elements.gallery.value?.map(item => item.url)}
          background={item.elements.background.value}
        />
        <ProductProperties
          reference={item.elements.specifications_reference.value.toString()}
          modelCase={item.elements.specifications_model_case.value}
          bezel={item.elements.specifications_bezel.value}
          waterResistance={item.elements.specifications_water_resistance.value}
          movement={item.elements.specifications_movement.value}
          calibre={item.elements.specifications_calibre.value}
          powerReserve={item.elements.specifications_power_reserve.value}
          bracelet={item.elements.specifications_bracelet.value}
          dial={item.elements.specifications_dial.value}
          certification={item.elements.specifications_certification.value}
          title={item.elements.title.value}
          sources={[
            getKontentImage(item.elements.specifications_image?.value[0]?.url, { width: 1000 })
          ]}
          placeholderSrc={getKontentImage(item.elements.specifications_image?.value[0]?.url, {
            width: 100,
            height: 100
          })}
        />
        {keyModelFeatures.map((item, key) => (
          <KeyModelFeature
            key={key}
            image={item.image}
            title={item.title}
            description={item.text}
            variant={key % 2 ? 'text-image' : 'image-text'}
          />
        ))}
        <ModelAvailability />
        <Box width="100%" maxWidth="90%" m="0 auto" mt="10vh">
          {familyInfo.item.elements.video.value[0] && (
            <Box mb={[rem(20), rem(50)]}>
              <VideoPlayer
                maxWidth="initial"
                placeholderSrc={
                  familyInfo.item.elements.placeholder?.value[0] &&
                  getKontentImage(familyInfo.item.elements.placeholder?.value[0]?.url, {
                    width: 50
                  })
                }
                images={[
                  getKontentImage(familyInfo.item.elements.placeholder?.value[0]?.url, {
                    width: 600
                  }),
                  getKontentImage(familyInfo.item.elements.placeholder?.value[0]?.url, {
                    width: 1000
                  }),
                  getKontentImage(familyInfo.item.elements.placeholder?.value[0]?.url, {
                    width: 1500
                  }),
                  getKontentImage(familyInfo.item.elements.placeholder?.value[0]?.url, {
                    width: 1920
                  })
                ]}
                videoSrc={familyInfo.item.elements.video?.value[0]?.url}
              />
            </Box>
          )}
          <Box width="100%" maxWidth={rem(650)} m="0 auto">
            <Text style={{ whiteSpace: 'pre-line' }}>
              {familyInfo.item.elements.description.value}
            </Text>
          </Box>
        </Box>

        <PushBanner
          image={familyInfo.item.elements.image}
          preTitle={familyInfo.item.elements.pretitle.value}
          title={familyInfo.item.elements.title_b224cf7.value}
          href={`/${lang}/rolex/${fid}`}
          buttonTitle={lang === 'nl' ? 'Bekijk alle modellen' : 'View all models'}
        />
        <ContactForm
          productName={`${item.elements.title.value} ${item.elements.url_slug.value}`}
          showStores
        ></ContactForm>
        {relatedProducts.items.length > 0 && (
          <RelatedProductsSlider
            title={messages.rolex.detail.mayAlsoLike}
            slides={relatedProducts.items.map(item => {
              return {
                href: `/${lang}/rolex/${item.elements.rolex_family?.value[0]?.name}/${item.elements.url_slug.value}`,
                title: item.elements.title.value,
                subtitle: item.elements.short_description.value,
                images: [
                  item.elements.preview_image_mobile?.value[0]?.url,
                  item.elements.preview_image_desktop?.value[0]?.url
                ]
                  .filter(t => t)
                  .map(url => getKontentImage(url, { width: 300 })),
                placeholderSrc: getKontentImage(item.elements.preview_image_mobile?.value[0]?.url, {
                  width: 100,
                  height: 100
                })
              }
            })}
          />
        )}
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ preview, params }) => {
  try {
    const langParam =
      params && params.lang
        ? typeof params.lang === 'string'
          ? params.lang
          : params.lang.pop()
        : 'nl'
    const language = langParam === 'sg' ? 'en' : langParam
    const urlSlug = params.pid as string
    const content = await getPageByUrlSlug<RolexProduct>({ language, urlSlug, preview })

    let familyInfo = null
    if (content.item.elements.rolex_family.value[0]) {
      const codename = `info_${content.item.elements.rolex_family.value[0].codename}`
      familyInfo = await getPageByCodename({ codename, lang: language, preview })
    }

    let relatedProducts = null
    if (content.item.elements.related_products.value) {
      relatedProducts = await getSpecifiedRolexProducts({
        products: content.item.elements.related_products.value.replace(' ', '').split(','),
        language,
        preview
      })
    }

    return {
      props: {
        ...content,
        familyInfo: familyInfo as SingleItemResponse<RolexFamilyInfoProductPage>,
        relatedProducts: relatedProducts as MultipleItemResponse<RolexProduct>,
        fid: params.fid
      }
    }
  } catch (error) {
    throw error
  }
}

export const getStaticPaths = async () => {
  const paths = await getCatchAllRolexProductPaths()
  return {
    fallback: false,
    paths
  }
}

export default RolexDetail
